// Dependency-free URL routing: the address is the source of truth for the
// displayed screen. Only "addressable" screens have a route (home, login,
// profile, stats, shop, admin, game). The phases within a game (current
// question, validation, scores) stay driven by socket events under
// /partie/:code.

import { useEffect, useState } from "react";

const GAME_PREFIX = "/partie/";

export const ROUTES = {
  home:    "/",
  auth:    "/connexion",
  profile: "/profil",
  stats:   "/stats",
  medals:  "/medailles",
  shop:    "/boutique",
  settings: "/reglages",
  admin:   "/admin",
  system:  "/administration",
  game:    (gameId) => `${GAME_PREFIX}${gameId}`,
};

// Normalised path (no trailing slash, query/hash stripped)
export function currentPath() {
  return window.location.pathname.replace(/\/+$/, "") || "/";
}

// Game code carried by the URL, or null if the path is not a game.
// Server codes are uppercase: a link typed in lowercase still works.
export function gameIdFromPath(path) {
  if (!path.startsWith(GAME_PREFIX)) return null;
  const code = path.slice(GAME_PREFIX.length).toUpperCase();
  return /^[A-Z0-9]{4,10}$/.test(code) ? code : null;
}

// Absolute URL of a game: this is what gets shared to invite people.
export function gameUrl(gameId) {
  return `${window.location.origin}${ROUTES.game(gameId)}`;
}

const listeners = new Set();
const notify = () => listeners.forEach(fn => fn());

// pushState/replaceState do not fire popstate: we notify listeners ourselves.
// pqDepth counts the entries pushed by the app, so we know whether a "back"
// can stay inside the app (goBack) or must fall back to a default route.
export function navigate(to, { replace = false } = {}) {
  const depth = window.history.state?.pqDepth ?? 0;
  const next = { ...(window.history.state || {}), pqDepth: replace ? depth : depth + 1 };
  if (replace) window.history.replaceState(next, "", to);
  else window.history.pushState(next, "", to);
  notify();
}

// "Integrated" back: walk up the browser history if the app pushed something
// onto it, otherwise go straight to the fallback route.
export function goBack(fallback = ROUTES.home) {
  if ((window.history.state?.pqDepth ?? 0) > 0) window.history.back();
  else navigate(fallback, { replace: true });
}

export function useRoute() {
  const [path, setPath] = useState(currentPath);
  useEffect(() => {
    const sync = () => setPath(currentPath());
    listeners.add(sync);
    window.addEventListener("popstate", sync);
    return () => {
      listeners.delete(sync);
      window.removeEventListener("popstate", sync);
    };
  }, []);
  return path;
}
